import { DefaultLayout } from '@c/layouts';
import ProductsPage from '@c/ProductsPage';
import SEO from '@c/SEO';
import { CategoryDocument } from '@util/firestore/categories';
import { getProducts } from '@util/firestore/products';
import { toSerializable } from '@util/serializeFirestore';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { PaginationFilters, ListingsFilters } from '@util/hooks/useProductsPage';
import { ProductDocument } from '@util/types/firestore/products';
import { Pagination } from '@util/types/paginator';
import { GetServerSideProps } from 'next';
import { ConditionDocument } from '@util/types/firestore/conditions';
import { BrandDocument } from '@util/types/firestore/brand';

interface ProductsProps {
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: PaginationFilters;
  initialSideFilters: ListingsFilters;
  categoryOptions: CategoryDocument[];
  conditionOptions: ConditionDocument[];
  brandOptions: BrandDocument[];
  pageNumber: number;
}

const Products = ({
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  categoryOptions,
  conditionOptions,
  brandOptions,
  pageNumber,
}: ProductsProps) => {
  // SEO metadata
  const pageTitle = pageNumber > 1
    ? `All Camera Gear & Photography Equipment - Page ${pageNumber}`
    : `All Camera Gear & Photography Equipment`;
  
  const pageDescription = `Explore our extensive collection of new and used cameras, lenses, and accessories. Find the perfect photography equipment from trusted sellers on GearFocus.`;
  
  return (
    <>
      <SEO 
        title={pageTitle}
        description={pageDescription}
        ogImage="/images/camera-collection.jpg"
        keywords={[
          'camera equipment', 'photography gear', 'used cameras', 'camera lenses', 
          'professional cameras', 'DSLR', 'mirrorless', 'camera accessories'
        ]}
      />
      
      <ProductsPage
        initialProducts={initialProducts}
        initialPageInfo={initialPageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        headerBreadcrumb={[
          { label: 'Home', href: '/' },
          { label: 'Products', href: '/products' }
        ]}
        pageType="all"
        categoryOptions={categoryOptions}
        conditionOptions={conditionOptions}
        brandOptions={brandOptions}
        slugToTitle={undefined}
        keyword={null}
        seller_id={null}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<ProductsProps> = async (
  context
) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );
  const args = await getProductQueryArgsFromContext(context);
  const response = await getProducts(args);

  const pageNumber = response.pageInfo.page || 1;

  return {
    // getProducts falls back to Firestore when a query can't use Algolia, and
    // those docs arrive raw — any Timestamp on them would fail serialization.
    props: toSerializable({
      categoryOptions: args.categoryOptions,
      conditionOptions: args.conditionOptions,
      brandOptions: args.brandOptions,
      initialProducts: response.results,
      initialPageInfo: response.pageInfo,
      initialTopFilters: {
        sort: args.sort,
        limit: args.limit,
        view: (context.query.view as 'list') || 'grid',
      },
      initialSideFilters: {
        minPrice: args.minPrice,
        maxPrice: args.maxPrice,
        category: args.categoryFilters,
        condition: args.conditionFilters,
        brand: args.brandFilters,
        deals: args.deals,
        useCases: args.useCases,
        //additional: args.additionalFilters
      },
      pageNumber
    }),
  };
};

Products.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default Products;
